import { Box } from "@mui/material";
import { v2, nums } from "../ui/v2Tokens";
import { bandTone } from "../ui/bandTone";
import { metricFigure, metricLabel, type LogMetric, type MonthSummary } from "./monthMetrics";
import { useNutritionDisplayMode } from "../../../../contexts/NutritionDisplayContext";

type Props = { summary: MonthSummary; metric: LogMetric };

const Tag = ({ fill, color, children }: { fill: string; color: string; children: React.ReactNode }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      height: 24,
      padding: "0 10px",
      borderRadius: "7px",
      fontSize: "11.5px",
      fontWeight: 700,
      background: fill,
      color,
      ...nums,
    }}
  >
    {children}
  </Box>
);

/**
 * The month in one line: how much of it the trainee reported at all, and how the days they did
 * report landed against the target.
 *
 * Coverage comes first on purpose. A trainer's first question is not "what was the average" but
 * "did they log" — an average over four days of thirty is a number about four days, and the bar
 * says so before the figure beside it can mislead.
 */
const MonthSummaryLine = ({ summary, metric }: Props) => {
  const mode = useNutritionDisplayMode();
  const label = metricLabel(metric, mode);
  const {
    reportedDays,
    totalDays,
    average,
    deviationPct,
    band,
    representative,
    counts,
    coverage,
    targetPerDay,
    targetChanged,
  } = summary;
  const averageFigure = average !== null ? metricFigure(average, metric, mode) : null;
  const targetFigure = targetPerDay ? metricFigure(targetPerDay, metric, mode) : null;

  return (
    <Box
      data-testid="month-summary"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "14px",
        flexWrap: "wrap",
        padding: "11px 14px",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
        <Box sx={{ fontSize: "19px", fontWeight: 800, color: v2.ink, ...nums }}>{reportedDays}</Box>
        {/* days ELAPSED, not the length of the month — "4 מתוך 30" on the 5th reads as a trainee
            who has barely logged, when they have logged four days out of five */}
        <Box sx={{ fontSize: "12px", color: v2.ink3, ...nums }}>מתוך {totalDays} ימים שנרשמו</Box>
      </Box>

      <Box
        aria-hidden
        sx={{
          display: "flex",
          height: 7,
          borderRadius: "99px",
          overflow: "hidden",
          background: v2.lineSoft,
          flex: { xs: "1 1 100%", sm: "0 0 190px" },
        }}
      >
        {coverage.map((segment) => (
          <Box
            key={segment.kind}
            title={`${segment.days} ימים`}
            sx={{
              width: `${segment.pct}%`,
              background:
                segment.kind === "future"
                  ? v2.lineSoft
                  : segment.kind === "unreported"
                    ? "repeating-linear-gradient(135deg, #E7EBEF 0 3px, #F6F8FA 3px 6px)"
                    : bandTone[segment.kind].text,
              opacity: segment.kind === "unreported" || segment.kind === "future" ? 1 : 0.85,
            }}
          />
        ))}
      </Box>

      {reportedDays === 0 ? (
        <Box sx={{ fontSize: "12px", color: v2.ink3 }}>אין דיווחים בחודש הזה</Box>
      ) : (
        <>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
            <Box sx={{ fontSize: "12px", color: v2.ink2, fontWeight: 500 }}>
              ממוצע {label} בימים שנרשמו
            </Box>
            <Box sx={{ fontSize: "19px", fontWeight: 800, color: v2.ink, ...nums }}>
              {averageFigure?.text}
            </Box>
            <Box sx={{ fontSize: "11.5px", color: v2.ink3 }}>{averageFigure?.unit}</Box>
          </Box>

          {/* Three days is the floor below which an average is a number about a trainee's worst or
              best day, not about their month. Saying so is more use than printing it anyway. */}
          {!representative ? (
            <Tag fill={v2.warnWash} color={v2.warn}>
              פחות מ-3 ימים — לא מייצג
            </Tag>
          ) : deviationPct !== null ? (
            <Tag fill={bandTone[band].fill} color={bandTone[band].text}>
              {deviationPct > 0 ? "+" : ""}
              {deviationPct}% מהיעד
            </Tag>
          ) : null}

          {/*
            Four bands, so four counts. A three-clause version left every day between +8% and +20%
            out of the sentence entirely, and a trainer adding the numbers up would find days
            missing from their own month.
          */}
          <Box sx={{ fontSize: "11.5px", color: v2.ink3, ...nums }}>
            <b>{counts.ok}</b> ימים ביעד · <b>{counts.warn}</b> מעל היעד · <b>{counts.over}</b> חריגות
            · <b>{counts.under}</b> מתחת ליעד
          </Box>
        </>
      )}

      <Box sx={{ marginInlineStart: "auto", fontSize: "11.5px", color: v2.ink4, ...nums }}>
        {targetChanged
          ? "היעד השתנה במהלך החודש"
          : targetFigure
            ? `יעד ${targetFigure.text} ${targetFigure.unit} ליום`
            : "לא הוגדר יעד"}
      </Box>
    </Box>
  );
};

export default MonthSummaryLine;
